import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { logger } from '../lib/logger';
import { trackSignUp } from '../lib/analytics';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setAuthenticatedUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);
        const sessionId = params.get('session_id');

        if (!sessionId) {
          toast.error('Invalid authentication response');
          navigate('/login');
          return;
        }

        const response = await authAPI.createSession(sessionId);
        const userData = response.data.user;

        // Update auth context first, then navigate. This makes the app
        // treat the user as signed-in on the very next render (no reload needed).
        setAuthenticatedUser(userData);

        // Honour purchase intent carried across the OAuth round-trip.
        const purchaseIntent = sessionStorage.getItem('auth_intent') === 'from_purchase';
        const sourceLabel = purchaseIntent ? 'from_purchase' : 'direct';
        // Fire the sign-up completion event only for genuinely new accounts.
        if (response.data.is_new_user) {
          trackSignUp(sourceLabel, 'google');
        }
        sessionStorage.removeItem('auth_intent');

        toast.success('Successfully signed in!');
        navigate(purchaseIntent ? '/cart' : '/dashboard', { replace: true, state: { user: userData } });
      } catch (error) {
        logger.error('Auth callback error:', error);
        toast.error('Authentication failed. Please try again.');
        navigate('/login');
      }
    };

    processAuth();
  }, [navigate, setAuthenticatedUser]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
        <p className="text-text_muted">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
